import { Box, Button, Grid, Link, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useMixpanel } from 'react-mixpanel-browser';

import { useDeliveryEndpoints, usePostAddDeliveryEndpoint, usePostDeleteDeliveryEndpoint } from '@/api/sofarApi';
import { FormInputText } from '@/components/form/FormInputText';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';

type IFormInput = {
    endpoint: string;
};

export const DeliverySection = () => {
    const { data: deliveryEndpoints } = useDeliveryEndpoints();
    const { mutateAsync: postAddEndpoint } = usePostAddDeliveryEndpoint();
    const { mutateAsync: postDeleteEndpoint } = usePostDeleteDeliveryEndpoint();
    const mixpanel = useMixpanel();
    const {
        control,
        handleSubmit,
        reset: resetAddEndpointForm,
        formState,
    } = useForm<IFormInput>({
        defaultValues: { endpoint: '' },
        mode: 'onChange',
    });

    useUnsavedChangesGuard(formState.isDirty, 'delivery-endpoints-section', {
        text: 'You have not yet added your endpoint.',
    });

    const handleAddEndpoint = async (data: IFormInput) => {
        try {
            await postAddEndpoint(data);
            mixpanel?.track('Sofar API Delivery Endpoint Added');
            resetAddEndpointForm();
        } catch (err) {
            console.error(err);
        }
    };

    const handleRemove = (endpoint: string) => async () => {
        try {
            await postDeleteEndpoint({ endpoint });
            mixpanel?.track('Sofar API Delivery Endpoint Removed');
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <>
            <Grid size={{ xs: 12 }}>
                <Typography variant="h4" gutterBottom>
                    Delivery Endpoints
                </Typography>
            </Grid>
            <Grid container spacing={4}>
                <Grid size={{ xs: 12, md: 7 }}>
                    <Typography variant="body1" gutterBottom>
                        When one of your Spotters generates new data, A POST request will be made to all of your
                        delivery endpoints. The new data will be included in the JSON body of the request.
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                        For details on the formatting of forwarded JSON POST requests, see our{' '}
                        <Link href="https://docs.sofarocean.com/spotter-and-smart-mooring/post-forwarding">
                            POST Forwarding API documentation
                        </Link>
                        .
                    </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 10, md: 5 }}>
                    <Stack spacing={2}>
                        <FormInputText
                            name="endpoint"
                            control={control}
                            label="New Delivery Endpoint"
                            inputFieldProps={{
                                id: 'endpoint',
                                type: 'text',
                                size: 'medium',
                            }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button variant="outlined" color="success" onClick={handleSubmit(handleAddEndpoint)}>
                                Add Endpoint
                            </Button>
                        </Box>
                    </Stack>
                </Grid>

                {deliveryEndpoints?.data?.endpoints.map((endpoint, i) => (
                    <Grid key={`${endpoint}-${i}`} size={{ xs: 12, sm: 10, md: 7 }}>
                        <Stack spacing={2}>
                            <FormInputText
                                name={`endpoint-${i}`}
                                control={control}
                                label="Delivery Endpoint"
                                inputFieldProps={{
                                    value: endpoint,
                                    id: `endpoint-${i}`,
                                    type: 'text',
                                    size: 'medium',
                                    disabled: true,
                                    sx: {
                                        '&& .MuiOutlinedInput-notchedOutline.MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'info.main',
                                        },
                                        '&& .MuiFormLabel-root.Mui-disabled': {
                                            color: 'info.main',
                                        },
                                        '&& .MuiOutlinedInput-input.Mui-disabled': {
                                            WebkitTextFillColor: 'black',
                                        },
                                    },
                                }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button variant="outlined" color="error" onClick={handleRemove(endpoint)}>
                                    Remove
                                </Button>
                            </Box>
                        </Stack>
                    </Grid>
                ))}
            </Grid>
        </>
    );
};
